import React, { useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { get } from "lodash";
import { CloseIcon } from "../../../arrow/components/Svgs/close-icon";
import { ThankYouModal } from "../thankyou-modal";
import "./contact-form-section.m.css";

const ContactFormSection = () => {
  const homePageData = useSelector((state) => get(state, ["qt", "config", "publisher-attributes","homePageData"], {}));
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    designation: "",
    city: "",
    agreeToTerms: false
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const [isButtonEnabled, setIsButtonEnabled] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^[0-9]{10}$/;
    return phoneRegex.test(phone.replace(/\s/g, ''));
  };

  const validateField = (name, value) => {
    let error = "";

    switch (name) {
      case "email":
        if (!validateEmail(value)) {
          error = "Enter a valid email";
        }
        break;
      case "phone":
        if (!validatePhone(value)) {
          error = "Enter a valid phone number";
        }
        break;
      case "name":
        if (!value.trim()) {
          error = "Field cannot be empty";
        }
        break;
      default:
        break;
    }

    return error;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newFormData = {
      ...formData,
      [name]: type === "checkbox" ? checked : value
    };
    setFormData(newFormData);

    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ""
      });
    }

    // Check if all required fields are filled and terms agreed
    const isComplete = newFormData.name &&
                      newFormData.email &&
                      newFormData.phone &&
                      newFormData.company &&
                      newFormData.designation &&
                      newFormData.city &&
                      newFormData.agreeToTerms;

    setIsButtonEnabled(isComplete);
  };


  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (loading) return;

      // Validate all fields before submission
      const newErrors = {};
      Object.keys(formData).forEach((key) => {
        if (key !== "agreeToTerms") {
          const error = validateField(key, formData[key]);
          if (error) {
            newErrors[key] = error;
          }
        }
      });

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
      }

      setLoading(true);

      try {
        const response = await fetch("/contact-us-smtp", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...formData, formType: "contact_us" }),
        });

        if (!response.ok) {
          throw new Error("Submission failed");
        }

        setSubmitted(true);
      } catch {
        alert("Something went wrong. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [formData, loading]
  );

  const handleTermsClick = (e) => {
    e.preventDefault();
    setIsTermsModalOpen(true);
  };

  const TermsModal = () => {
    if (!isTermsModalOpen) return null;

    return (
      <div styleName="terms-modal-overlay" onClick={() => setIsTermsModalOpen(false)}>
        <div styleName="terms-modal-content" onClick={(e) => e.stopPropagation()}>
          <div styleName="terms-modal-header">
            <button
              styleName="close-button"
              onClick={() => setIsTermsModalOpen(false)}
              aria-label="Close modal"
            >
              <CloseIcon />
            </button>
          </div>
          <div styleName="terms-modal-body">
            <h2 styleName="terms-title">Standard Disclaimer</h2>
            <div styleName="terms-content">
              <p styleName="terms-paragraph">
                The information presented on this website reflects IMA India's assessment of economic, business and policy conditions prevailing at the time of publication. In many instances, it also provides a view on how the environment is likely to evolve in the short-to-medium term.
              </p>
              <p styleName="terms-paragraph">
                The content is analytical and forward-looking in nature. While IMA India has taken the greatest possible care to ensure that the information published is accurate to the best of its knowledge, no representation or warranty is made as to its completeness, precision or continuing validity.
              </p>
              <p styleName="terms-paragraph">
                The material is not intended to constitute, and must not be relied upon as, the basis for investment decisions, strategic commitments or any other commercial, financial or policy actions. IMA India does not assume any liability or responsibility for decisions taken, or outcomes arising, from the use of or reliance on the information contained on this website.
              </p>
              <p styleName="terms-paragraph">
                Unless explicitly stated otherwise, the content reflects general market assessments and aggregated insights rather than advice tailored to any specific organisation, sector or jurisdiction. Any references to specific organisations, sectors, practices or trends are illustrative in nature and do not imply endorsement, recommendation or evaluation by IMA. Event discussions, survey findings and aggregated insights are presented in anonymised and generalised form unless explicitly stated otherwise.
              </p>
              <p styleName="terms-paragraph">
                All content on this website is the intellectual property of IMA India and is meant primarily for the use of members of IMA India's Forums. No part may be reproduced, distributed or transmitted, in whole or in part, without prior written consent from IMA India.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div styleName="contact-form-section">
      <div styleName="container">
        <div styleName="content">
          <div styleName="left-column">
            <div styleName="headline">
              <h2 styleName="section-heading">
                <span styleName="highlight-text">Connect with your true peers.</span> Start here
              </h2>
              <p styleName="description">
                Fill out the form, and our team will get in touch to guide you through the next steps.
              </p>
            </div>

            <div styleName="contact-details">
              <p styleName="contact-label">or contact us to know more</p>
              {homePageData?.contactEmail && <div styleName="contact-item">
                <span styleName="contact-label-small">Email</span>
                <span styleName="contact-value">{homePageData?.contactEmail}</span>
              </div>}
              {homePageData?.contactPhone && <div styleName="contact-item">
                <span styleName="contact-label-small">Phone</span>
                <span styleName="contact-value">{homePageData?.contactPhone}</span>
              </div>}
            </div>
          </div>

          <div styleName="right-column">
            <form styleName="form" onSubmit={handleSubmit}>
              <div styleName="form-field">
                <input
                  type="text"
                  name="name"
                  placeholder="Full Name*"
                  value={formData.name}
                  onChange={handleInputChange}
                  styleName="form-input"
                />
                {errors.name && <p styleName="error-message">{errors.name}</p>}
              </div>

              <div styleName="form-row">
                <div styleName="form-field">
                  <input
                    type="text"
                    name="email"
                    placeholder="Email*"
                    value={formData.email}
                    onChange={handleInputChange}
                    styleName="form-input"
                  />
                  {errors.email && <p styleName="error-message">{errors.email}</p>}
                </div>
                <div styleName="form-field">
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Phone*"
                    value={formData.phone}
                    onChange={handleInputChange}
                    styleName="form-input"
                  />
                  {errors.phone && <p styleName="error-message">{errors.phone}</p>}
                </div>
              </div>

              <div styleName="form-row">
                <div styleName="form-field">
                  <input
                    type="text"
                    name="company"
                    placeholder="Company*"
                    value={formData.company}
                    onChange={handleInputChange}
                    styleName="form-input"
                  />
                  {errors.company && <p styleName="error-message">{errors.company}</p>}
                </div>
                <div styleName="form-field">
                  <input
                    type="text"
                    name="designation"
                    placeholder="Designation*"
                    value={formData.designation}
                    onChange={handleInputChange}
                    styleName="form-input"
                  />
                  {errors.designation && <p styleName="error-message">{errors.designation}</p>}
                </div>
              </div>

              <div styleName="form-field">
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  placeholder="City*"
                  styleName="form-input"
                />
                {errors.city && <p styleName="error-message">{errors.city}</p>}
              </div>

              <div styleName="checkbox-field">
                <input
                  type="checkbox"
                  id="agreeToTerms"
                  name="agreeToTerms"
                  checked={formData.agreeToTerms}
                  onChange={handleInputChange}
                  styleName="checkbox"
                />
                <label styleName="checkbox-label">
                  I agree to IMA India's <strong onClick={handleTermsClick}>Terms & Conditions</strong> and consent to be contacted
                </label>
              </div>

              <button
                type="submit"
                styleName={`submit-button ${isButtonEnabled ? 'submit-button-enabled' : ''}`}
                disabled={!isButtonEnabled || loading}
              >
                {loading ? "Submitting..." : "Request a call"}
              </button>
            </form>
          </div>
        </div>

        {/* Mobile contact details */}
        <div styleName="mobile-contact-details">
          <p styleName="contact-label">or contact us to know more</p>
          <div styleName="contact-item">
            <span styleName="contact-label-small">Email</span>
            <span styleName="contact-value">{homePageData?.contactEmail}</span>
          </div>
          <div styleName="contact-item">
            <span styleName="contact-label-small">Phone</span>
            <span styleName="contact-value">{homePageData?.contactPhone}</span>
          </div>
        </div>
      </div>

      <TermsModal />

      <ThankYouModal
        isVisible={submitted}
        onClose={() => {
          setSubmitted(false);
          setFormData({
            name: "",
            email: "",
            phone: "",
            company: "",
            designation: "",
            city: "",
            agreeToTerms: false
          });
          setErrors({});
          setIsButtonEnabled(false);
        }}
        title="Thank you for your request"
        subtitle="An IMA India representative will be in touch shortly."
      />
    </div>
  );
};

export { ContactFormSection };